import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LifeBuoy, Save, Mail } from "lucide-react";

const URG = { low: "text-muted-industrial", normal: "text-sky-400", high: "text-amber-400", critical: "text-red-500 font-black" };
const STATUSES = ["open", "in_progress", "resolved"];

export default function SupportInbox() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("open");
  const [mailbox, setMailbox] = useState("");
  const [notes, setNotes] = useState({});

  const load = () => api.get(`/superadmin/support${filter && filter !== "all" ? `?status=${filter}` : ""}`).then((r) => setTickets(r.data)).catch(() => toast.error("Super-admin access required"));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line
  useEffect(() => { api.get("/superadmin/settings/support-mailbox").then((r) => setMailbox(r.data.value || "")).catch(() => {}); }, []);

  const saveMailbox = async () => {
    try { await api.put("/superadmin/settings/support-mailbox", { value: mailbox }); toast.success("Support mailbox saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const patch = async (t, body) => {
    try { await api.patch(`/superadmin/support/${t.id}`, body); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="support-inbox-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><div className="label-tag">ARMORBATCH GLOBAL · SUPER-ADMIN</div><h1 className="heading text-3xl font-black uppercase flex items-center gap-2"><LifeBuoy className="w-7 h-7 text-red-500" />Support Inbox</h1></div>
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-muted-industrial" />
          <Input data-testid="support-mailbox-input" value={mailbox} onChange={(e) => setMailbox(e.target.value)} placeholder="support@yourcompany.com" className="rounded-none bg-transparent border-industrial w-64 text-sm" />
          <Button data-testid="support-mailbox-save" variant="outline" className="rounded-none" onClick={saveMailbox}><Save className="w-4 h-4 mr-1" />Save</Button>
        </div>
      </div>
      <div className="flex gap-1">
        {["open", "in_progress", "resolved", "all"].map((s) => (
          <button key={s} type="button" data-testid={`support-filter-${s}`} onClick={() => setFilter(s)} className={`mono text-[10px] uppercase tracking-wider px-3 py-1.5 border ${filter === s ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}>{s.replace("_", " ")}</button>
        ))}
      </div>
      <div className="space-y-2">
        {tickets.map((t) => (
          <div key={t.id} data-testid={`ticket-${t.id}`} className="border border-industrial bg-surface p-4 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <span className="mono text-xs font-bold">{t.id}</span>
                <span className={`mono text-[10px] uppercase ${URG[t.urgency] || ""}`}>{t.urgency}</span>
                <span className="mono text-[10px] uppercase border border-industrial px-1">{t.category}</span>
                <span className="mono text-[10px] text-muted-industrial">{new Date(t.created_at).toLocaleString()}</span>
                <span className="mono text-[10px] text-muted-industrial">email: {t.email_status}</span>
              </div>
              <div className="text-sm font-bold">{t.org_name} <span className="text-muted-industrial font-normal">· {t.user_name} ({t.user_role}) · {t.user_email}{t.callback_phone ? ` · ☎ ${t.callback_phone}` : ""}</span></div>
              <div className="text-xs text-muted-industrial mono">page {t.page}</div>
              <p className="mt-2 text-sm whitespace-pre-wrap">{t.message}</p>
            </div>
            <div className="space-y-2">
              <div className="flex gap-1">
                {STATUSES.map((s) => (
                  <button key={s} type="button" data-testid={`ticket-${t.id}-status-${s}`} onClick={() => patch(t, { status: s })} className={`flex-1 mono text-[10px] uppercase px-1 py-1 border ${t.status === s ? "border-primary-orange text-primary-orange" : "border-industrial text-muted-industrial"}`}>{s.replace("_", " ")}</button>
                ))}
              </div>
              <textarea data-testid={`ticket-${t.id}-note`} rows={3} value={notes[t.id] ?? t.internal_note ?? ""} onChange={(e) => setNotes({ ...notes, [t.id]: e.target.value })} placeholder="Internal note" className="w-full bg-transparent border border-industrial p-2 text-xs" />
              <Button data-testid={`ticket-${t.id}-save-note`} size="sm" variant="outline" className="rounded-none text-xs w-full" onClick={() => patch(t, { internal_note: notes[t.id] ?? "" })}>Save note</Button>
            </div>
          </div>
        ))}
        {!tickets.length && <div className="text-muted-industrial text-sm" data-testid="support-empty">No {filter === "all" ? "" : filter.replace("_", " ")} tickets.</div>}
      </div>
    </div>
  );
}
